'use client';

import { useState } from 'react';
import * as Sentry from '@sentry/react';
import { toast } from 'sonner';
import { supabase } from '@/lib/supabaseClient';

interface useCambiarPassword {
  newPassword: string;
  confirmPassword: string;
  loading: boolean;
  tokenExpired: boolean;
  setNewPassword: (v: string) => void;
  setConfirmPassword: (v: string) => void;
  cambiar: () => Promise<boolean>;
  reset: () => void;
}

export function useCambiarPassword(): useCambiarPassword {
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [tokenExpired, setTokenExpired] = useState(false);

  /* ============================================================
   * Validaciones
   * ============================================================ */
  const validar = () => {
    if (newPassword !== confirmPassword) {
      toast.error('Las contraseñas no coinciden.');
      return false;
    }

    if (newPassword.length < 6) {
      toast.error('Debe tener al menos 6 caracteres.');
      return false;
    }

    return true;
  };

  /* ============================================================
   * Resetear formulario
   * ============================================================ */
  const reset = () => {
    setNewPassword('');
    setConfirmPassword('');
  };

  /* ============================================================
   * Cambiar contraseña (Supabase)
   * ============================================================ */
  const cambiar = async (): Promise<boolean> => {
    if (!validar()) return false;

    setLoading(true);

    const { error } = await supabase.auth.updateUser({
      password: newPassword,
    });

    setLoading(false);

    if (error) {
      // Token caducado → se usa en SetNewPassword.tsx
      if (
        error.message.includes('expired') ||
        error.message.includes('Invalid') ||
        error.message.includes('expired token')
      ) {
        setTokenExpired(true);
        return false;
      }

      // Contraseña igual que la actual
      if (error.code === 'same_password') {
        toast.error('La nueva contraseña debe ser diferente a la actual.');
        return false;
      }

      // Otros errores inesperados
      Sentry.captureException(error); // 👈
      toast.error('No se pudo cambiar la contraseña.');
      return false;
    }

    toast.success('Contraseña actualizada correctamente.');
    reset();
    return true;
  };

  return {
    newPassword,
    confirmPassword,
    loading,
    tokenExpired,
    setNewPassword,
    setConfirmPassword,
    cambiar,
    reset,
  };
}
